import type { ClientOutputs } from '@botpress/client'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  useSidebar,
} from '@bpinternal/ui-kit-next'
import { useParams, useRouter } from '@tanstack/react-router'
import Fuse from 'fuse.js'
import { Loader2, Plus, Search } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useDebounce } from 'react-use'
import { BotIcon } from '~/components/BotIcon'
import { useCreateBotFlow } from '~/hooks/bots'

type Props = { className?: string; bots: ClientOutputs['listBots']['bots']; children: ReactNode; asChild?: boolean }

export const BotsDropdown = ({ bots, children, asChild = true }: Props) => {
  const navigate = useRouter().navigate
  const { workspaceId, botId } = useParams({ strict: false })
  const { isMobile, setOpenMobile } = useSidebar()
  const { startCreateBotFlow, isCreatingBot } = useCreateBotFlow({ workspaceId })
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const filteredBots = useMemo(() => {
    if (!debouncedQuery.trim()) return bots || []

    const fuse = new Fuse(bots || [], {
      keys: ['name', 'id'],
      threshold: 0.3,
      includeScore: true,
    })

    const results = fuse.search(debouncedQuery)
    return results.map((result) => result.item)
  }, [bots, debouncedQuery])

  useDebounce(
    () => {
      setDebouncedQuery(searchQuery)
    },
    100,
    [searchQuery]
  )

  if (!workspaceId) {
    return null
  }

  const handleBotSelection = (selectedBotId: string) => {
    if (isMobile) {
      setOpenMobile(false)
    }
    navigate({ to: '/workspaces/$workspaceId/bots/$botId', params: { workspaceId, botId: selectedBotId } })
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          setSearchQuery('')
        }
      }}
    >
      <DropdownMenuTrigger asChild={asChild}>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="flex min-w-60 flex-col"
        align="start"
        style={isMobile ? undefined : { maxHeight: 'min(50vh, var(--radix-dropdown-menu-content-available-height))' }}
      >
        <div className="relative">
          <Search className="absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search bots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // Prevent the dropdown's built-in keyboard navigation from auto-selecting items
              e.stopPropagation()
            }}
            className="h-8 border-0 bg-transparent pl-8 shadow-none focus:border-0 focus:ring-0 focus-visible:ring-0"
            autoFocus
          />
        </div>
        <DropdownMenuSeparator />
        <div className="flex-1 overflow-y-auto">
          {filteredBots?.map((bot) => {
            return (
              <DropdownMenuItem key={bot.id} disabled={bot.id === botId} onClick={() => handleBotSelection(bot.id)}>
                <BotIcon id={bot.id} size={'2'} />
                <span className="truncate">{bot.name}</span>
              </DropdownMenuItem>
            )
          })}
          {filteredBots?.length === 0 && searchQuery && (
            <DropdownMenuItem disabled>
              <span className="text-muted-foreground">No bots found</span>
            </DropdownMenuItem>
          )}
        </div>
        <div className="sticky bottom-0 bg-popover">
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setOpen(false)
              startCreateBotFlow()
            }}
            onSelect={(e) => e.preventDefault()}
            disabled={isCreatingBot}
          >
            {isCreatingBot ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                <span>Create Bot</span>
              </>
            )}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
